<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div>
  <vl-layer-vector>
    <vl-source-vector :ident="drawSourceName" ref="olSourceDrawTarget" />
  </vl-layer-vector>

  <vl-interaction-draw
    v-if="nbActiveLayers > 0 || drawCorrection"
    ref="olDrawInteraction"
    :source="drawSourceName"
    :type="drawType"
    :freehand="drawFreehand"
    :freehand-condition="undefined"
    :geometry-function="drawGeometryFunction"
    @drawend="drawEndHandler"
    @mounted="onDrawMounted"
  />
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';

import Polygon, {fromCircle as polygonFromCircle} from 'ol/geom/Polygon';
import WKT from 'ol/format/WKT';

import {Annotation, AnnotationType, Ims} from '@/api';
import {Action} from '@/utils/annotation-utils.js';
import {updateAnnotationLinkProperties} from '@/utils/annotation-utils';
import {isVRBrowser} from '@/utils/vr';

export default {
  name: 'draw-interaction',
  props: {
    index: String
  },
  data() {
    return {
      format: new WKT()
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    rotation() {
      return this.imageWrapper.view.rotation;
    },
    termsToAssociate() {
      return this.imageWrapper.draw.termsNewAnnots;
    },
    tracksToAssociate() {
      return this.imageWrapper.draw.tracksNewAnnots;
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    imageGroupId() {
      return this.$store.getters[this.imageModule + 'imageGroupId'];
    },
    slice() {
      // Cannot draw on multiple slices at same time
      return (this.imageWrapper.activeSlices) ? this.imageWrapper.activeSlices[0] : null;
    },
    activeTool() {
      return this.imageWrapper.draw.activeTool;
    },
    activeEditTool() {
      return this.imageWrapper.draw.activeEditTool;
    },
    selectedFeature() {
      return this.$store.getters[this.imageModule + 'selectedFeature'];
    },
    drawType() {
      switch (this.activeTool) {
        case 'point':
          return 'Point';
        case 'line':
        case 'freehand-line':
          return 'LineString';
        case 'magic-wand':
        case 'rectangle':
        case 'circle':
          return 'Circle';
        case 'polygon':
        case 'freehand-polygon':
        case 'select': // correct mode
          return 'Polygon';
        default:
          return ''; // Should not happen
      }
    },
    drawCorrection() {
      return this.activeTool === 'select';
    },
    drawFreehand() {
      // box/circle tools draw on a left press-drag-release (freehand) instead of click-click,
      // so a left-drag draws the shape rather than panning the image
      return ['freehand-polygon', 'freehand-line', 'rectangle', 'circle', 'magic-wand'].includes(this.activeTool)
        || this.drawCorrection;
    },
    drawGeometryFunction() {
      if (!['magic-wand', 'rectangle'].includes(this.activeTool)) {
        return;
      }

      return (coordinates, geometry) => {
        let rotatedCoords = this.rotateCoords(coordinates, this.rotation);

        let [firstCorner, thirdCorner] = rotatedCoords;
        let secondCorner = [thirdCorner[0], firstCorner[1]];
        let fourthCorner = [firstCorner[0], thirdCorner[1]];

        // live width/height while drawing a rectangle (side lengths in image pixels,
        // rotation-invariant) → shown in the top-left overlay by ImsImage
        if (this.activeTool === 'rectangle') {
          this.$eventBus.$emit('liveDrawSize', this.formatDrawSize(
            Math.abs(thirdCorner[0] - firstCorner[0]),
            Math.abs(thirdCorner[1] - firstCorner[1])
          ));
        }

        let rotatedBoxCoordinates = [firstCorner, secondCorner, thirdCorner, fourthCorner, firstCorner];
        let boxCoordinates = [this.rotateCoords(rotatedBoxCoordinates, -this.rotation)];

        if (geometry) {
          geometry.setCoordinates(boxCoordinates);
        } else {
          geometry = new Polygon(boxCoordinates);
        }
        return geometry;
      };
    },
    layers() {
      return this.imageWrapper.layers.selectedLayers || [];
    },
    activeLayers() {
      return this.layers.filter(layer => layer.drawOn);
    },
    nbActiveLayers() {
      return this.activeLayers.length;
    },
    drawSourceName() {
      return `draw-target-${this.index}`;
    }
  },

  watch: {
    activeTool() {
      this.$refs.olDrawInteraction.scheduleRecreate();
      this.$eventBus.$emit('liveDrawSize', null); // clear the live W×H overlay when switching tools
    }
  },

  methods: {
    // px side lengths → {width, height, unit} for the live drawing overlay. Shown in pixels
    // while drawing (the panel can switch a saved rectangle to µm/mm afterwards).
    formatDrawSize(widthPx, heightPx) {
      return {width: widthPx.toFixed(0), height: heightPx.toFixed(0), unit: 'px'};
    },

    rotateCoords(coords, theta) {
      let cosTheta = Math.cos(theta);
      let sinTheta = Math.sin(theta);
      return coords.map(([x, y]) => [x * cosTheta + y * sinTheta, -x * sinTheta + y * cosTheta]);
    },

    clearDrawnFeatures() {
      this.$refs.olSourceDrawTarget.clear(true);
    },

    async drawEndHandler({feature}) {
      this.$eventBus.$emit('liveDrawSize', null); // drawing finished → hide the live W×H overlay

      // Tremor handling (VR headset only): freehand drawing traces every pointer sample, so a shaky
      // hand inside the Meta Quest browser yields a jagged, self-intersecting outline. Simplify +
      // smooth ONLY the freehand strokes; exact tools (rectangle/circle/magic-wand) are untouched.
      // Gated to the headset browser so laptop drawing is byte-identical to before.
      if (isVRBrowser()
        && (this.activeTool === 'freehand-polygon' || this.activeTool === 'freehand-line' || this.drawCorrection)) {
        this.smoothFreehandGeometry(feature);
      }

      if (this.drawCorrection) {
        await this.endCorrection(feature);
      } else if (this.nbActiveLayers > 0) {
        await this.endDraw(feature);
        // After every completed shape, drop back to the Select tool (user request - was
        // VR-only): drawing is a one-shot action, and staying armed meant a stray click
        // created an accidental second annotation.
        this.$store.dispatch(this.imageModule + 'activateTool', 'select');
      }

      this.clearDrawnFeatures();
    },

    // VR only: a controller laser trembles between trigger press and release, exceeding OpenLayers'
    // 6px clickTolerance, so taps never register as vertices (no sketch point appears, the polygon
    // can't be started). Widen the Draw click tolerance on the headset. Laptop is untouched.
    onDrawMounted() {
      if (!isVRBrowser()) {
        return;
      }
      let draw = this.$refs.olDrawInteraction && this.$refs.olDrawInteraction.$interaction;
      if (draw) {
        let tolerance = 24;
        draw.squaredClickTolerance_ = tolerance * tolerance;
      }
    },

    // current map resolution (image px per screen px); tremor is ~constant in screen px, so scaling
    // tolerances by resolution keeps the smoothing consistent at every zoom level.
    currentResolution() {
      try {
        let interaction = this.$refs.olDrawInteraction && this.$refs.olDrawInteraction.$interaction;
        let map = interaction && interaction.getMap && interaction.getMap();
        let view = map && map.getView();
        let res = view && view.getResolution();
        if (res && res > 0) {
          return res;
        }
      } catch (e) {
        // fall through to a safe default
      }
      return 1;
    },

    // Douglas-Peucker simplify (drop tremor jitter) then Chaikin corner-cutting (round jaggedness).
    smoothFreehandGeometry(feature) {
      let geometry = feature.getGeometry();
      if (!geometry) {
        return;
      }
      let type = geometry.getType();
      if (type !== 'Polygon' && type !== 'LineString') {
        return;
      }

      let res = this.currentResolution();
      let tolerance = Math.max(res * 2.0, 1e-6); // ≈2 screen px of jitter removed
      let simplified = geometry.simplify(tolerance);
      if (!simplified || simplified.getType() !== type) {
        simplified = geometry; // simplification degenerated → keep the original
      }

      if (type === 'Polygon') {
        let rings = simplified.getCoordinates();
        let ring = rings && rings[0];
        if (ring && ring.length >= 4) {
          let smooth = this.chaikinSmooth(ring, true, 2);
          if (smooth.length >= 4) {
            simplified.setCoordinates([smooth]);
          }
        }
      } else {
        let line = simplified.getCoordinates();
        if (line && line.length >= 2) {
          let smooth = this.chaikinSmooth(line, false, 2);
          if (smooth.length >= 2) {
            simplified.setCoordinates(smooth);
          }
        }
      }

      feature.setGeometry(simplified);
    },

    // Chaikin corner-cutting smoothing. For a closed ring the duplicate closing point is dropped
    // during processing and re-added at the end so the WKT polygon stays valid.
    chaikinSmooth(coords, closed, iterations) {
      let pts = coords.slice();
      if (closed && pts.length > 1
        && pts[0][0] === pts[pts.length - 1][0] && pts[0][1] === pts[pts.length - 1][1]) {
        pts = pts.slice(0, -1);
      }

      for (let it = 0; it < iterations; it++) {
        if (pts.length < 3) {
          break;
        }
        let next = [];
        let n = pts.length;
        if (!closed) {
          next.push(pts[0]);
        }
        let limit = closed ? n : n - 1;
        for (let i = 0; i < limit; i++) {
          let p0 = pts[i];
          let p1 = pts[(i + 1) % n];
          next.push([0.75 * p0[0] + 0.25 * p1[0], 0.75 * p0[1] + 0.25 * p1[1]]);
          next.push([0.25 * p0[0] + 0.75 * p1[0], 0.25 * p0[1] + 0.75 * p1[1]]);
        }
        if (!closed) {
          next.push(pts[n - 1]);
        }
        pts = next;
      }

      if (closed && pts.length > 0) {
        pts = pts.concat([pts[0]]); // re-close the ring
      }
      return pts;
    },

    async endDraw(drawnFeature) {
      this.activeLayers.forEach(async (layer, idx) => {
        let annot = new Annotation({
          location: this.getWktLocation(drawnFeature),
          image: this.image.id,
          slice: this.slice.id,
          user: layer.id,
          term: this.termsToAssociate,
          track: this.tracksToAssociate
        });

        try {
          await annot.save();
          annot.userByTerm = this.termsToAssociate.map(term => ({term, user: [this.currentUser.id]}));
          annot.imageGroup = this.imageGroupId;
          updateAnnotationLinkProperties(annot);
          this.$eventBus.$emit('addAnnotation', annot);
          if (idx === this.nbActiveLayers - 1) {
            this.$eventBus.$emit('selectAnnotation', {index: this.index, annot});
          }

          this.$store.commit(this.imageModule + 'addAction', {annot, type: Action.CREATE});
        } catch (err) {
          console.log(err);
          this.$notify({type: 'error', text: this.$t('notif-error-annotation-creation')});
        }

        if (this.activeTool === 'magic-wand') {
          try {
            const annotationId = annot.id;
            const annotation = (await Ims.instance.api.post(`annotations/${annotationId}/refine`)).data;

            this.$eventBus.$emit('editAnnotation', annotation);
            this.$eventBus.$emit('reloadAnnotationCrop', annotation);
            this.$notify({type: 'success', text: 'Successful SAM Processing !'});
          } catch (error) {
            console.error(error);
            this.$notify({type: 'error', text: 'Error in SAM Processing.'});
          }
        }
      });
    },

    async endCorrection(feature) {
      if (!this.selectedFeature) {
        return;
      }

      try {
        let annot = this.selectedFeature.properties.annot;
        let correctedAnnot = await Annotation.correctAnnotations({
          location: this.getWktLocation(feature),
          review: annot.type === AnnotationType.REVIEWED,
          remove: (this.activeEditTool === 'correct-remove'),
          annotation: annot.id
        });
        if (correctedAnnot) {
          correctedAnnot.userByTerm = annot.userByTerm; // copy terms from initial annot
          correctedAnnot.track = annot.track;
          correctedAnnot.annotationTrack = annot.annotationTrack;
          correctedAnnot.group = annot.group;
          correctedAnnot.annotationLink = annot.annotationLink;
          correctedAnnot.imageGroup = annot.imageGroup;
          this.$store.commit(this.imageModule + 'addAction', {annot: correctedAnnot, type: Action.UPDATE});
          this.$eventBus.$emit('editAnnotation', correctedAnnot);
          this.$eventBus.$emit('reloadAnnotationCrop', annot);
        }
      } catch (err) {
        console.log(err);
        this.$notify({type: 'error', text: this.$t('notif-error-annotation-correction')});
      }
    },

    getWktLocation(feature) {
      // transform circle to circular polygon
      let geometry = feature.getGeometry();
      if (geometry.getType() === 'Circle') {
        feature.setGeometry(polygonFromCircle(geometry));
      }
      return this.format.writeFeature(feature);
    },
  },
  beforeDestroy() {
    this.$eventBus.$emit('liveDrawSize', null); // ensure the live overlay never lingers
  }
};
</script>
